<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section class="widget-section widget-section--filter flex no-wrap items-center q-mb-lg">
      <q-form ref="form" class="full-width">
        <div class="row no-wrap full-width q-col-gutter-sm">
          <div class="col">
            <q-input
              v-model="filters.uuid"
              label="UUID"
              outlined
              dense
              clearable
              @update:model-value="filterTable"
              @clear="reloadWorkflows()"
              :rules="[validateUuid]"
            ></q-input>
          </div>
          <div class="col">
            <q-input
              v-model="filters.itemId"
              label="Item ID"
              outlined
              dense
              clearable
              @update:model-value="filterTable"
              @clear="reloadWorkflows()"
              :rules="[validateUuid]"
            ></q-input>
          </div>
          <div class="col">
            <StatusSelect
              v-model="filters.status"
              @update:model-value="filterTable"
              class="full-width"
              @clear="reloadWorkflows()"
            />
          </div>
          <div class="col">
            <WorkflowNameSelect
              v-model="filters.workflowIds"
              @update:model-value="onWorkflowIdsUpdate"
              class="full-width"
              @clear="reloadWorkflows()"
            ></WorkflowNameSelect>
          </div>
          <div class="col">
            <q-input
              v-model="filters.inputVariables"
              label="Input Variables"
              outlined
              dense
              clearable
              @update:model-value="filterTable"
              @clear="reloadWorkflows()"
            ></q-input>
          </div>
        </div>
        <div class="row no-wrap full-width q-col-gutter-sm q-mb-sm">
          <div class="col col-2 flex no-wrap q-col-gutter-sm">
            <q-checkbox
              toggle-indeterminate
              @update:model-value="() => $nextTick(() => reloadWorkflows())"
              v-model="filters.cancelling"
              label="Cancelling"
            />
          </div>
          <div class="col flex no-wrap q-col-gutter-sm">
            <DatePicker
              v-model="filters.dateFrom"
              @update:model-value="filterTable"
              label="Created after"
              class="full-width"
              @clear="
                () => {
                  filters.dateFrom = undefined;
                }
              "
            />
          </div>
          <div class="col flex no-wrap q-col-gutter-sm">
            <DatePicker
              v-model="filters.dateTo"
              @update:model-value="filterTable"
              label="Created before"
              class="full-width"
              @clear="
                () => {
                  filters.dateTo = undefined;
                }
              "
            />
          </div>
        </div>
      </q-form>
      <q-icon @click="resetFilters" name="engp:engp-ico-restart" class="q-ml-md cursor-pointer" size="20px">
        <q-tooltip>Reset</q-tooltip>
      </q-icon>
    </section>

    <section class="widget-section widget-section--table">
      <q-table
        :columns="cols"
        flat
        :rows="rows"
        class="external-dns-table fixed-table"
        row-key="id"
        ref="workflowTable"
        binary-state-sort
        :rows-per-page-options="[20, 50, 100]"
        v-model:pagination="pagination"
        :loading="loading"
        @request="loadWorkflows"
      >
        <template v-slot:body-cell-actions="props">
          <q-td :props="props">
            <q-btn
              flat
              round
              size="12px"
              icon="sym_r_account_tree"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :ripple="false"
              @click="goToWorkflow($event, props.row.uuid)"
            >
              <q-tooltip>Show workflow details</q-tooltip>
            </q-btn>
            <WorkflowsBtn @showWorkflow="showRelatedWorkflows(props.row)" :relatedWorkflowStatuses="[]" />
            <q-btn
              v-if="!['completed', 'abandoned', 'terminated'].includes(props.row.status)"
              flat
              round
              size="12px"
              icon="sym_r_refresh"
              style="width: 30px; height: 40px"
              :ripple="false"
              class="btn--no-hover"
              :loading="statusLoadingWorkflowId === props.row.uuid"
              @click="reloadStatus(props.row.uuid)"
            >
              <q-tooltip>Refresh workflow status</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              size="9px"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :ripple="false"
              icon="engp:engp-ico-copy"
              @click.stop="copyUuid(props.row.uuid)"
            >
              <q-tooltip>Copy uuid</q-tooltip>
            </q-btn>
            <q-btn
              v-if="props.row.status === 'not started'"
              flat
              round
              size="12px"
              icon="sym_r_delete"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :ripple="false"
              @click="confirmAbandon(props.row.uuid)"
            >
              <q-tooltip>Abandon workflow</q-tooltip>
            </q-btn>
          </q-td>
        </template>
        <template v-slot:body-cell-workflowId="props">
          <q-td style="width: 30px" align="center" :props="props">
            <span class="instance-key cursor-pointer" @click="goToWorkflow($event, props.row.uuid)">{{
              props.row.workflowId
            }}</span>
          </q-td>
        </template>
        <template v-slot:body-cell-processInstanceKey="props">
          <q-td style="width: 30px" align="center" :props="props">
            <span class="instance-key cursor-pointer" @click="goToSco(props.row.processInstanceKey)">{{
              props.row.processInstanceKey
            }}</span>
          </q-td>
        </template>
        <template v-slot:body-cell-status="props">
          <q-td style="width: 30px" :props="props">
            <Status :workflow="props.row" tableView />
          </q-td>
        </template>
        <template v-slot:no-data>
          <div class="flex full-width column items-center q-py-lg">
            <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
            <span class="block" style="font-size: 14px">No items were found.</span>
          </div>
        </template>
      </q-table>
    </section>
  </q-page>

  <q-dialog v-model="showConfirm" class="single-service-page" persistent>
    <q-card style="min-width: 768px; border-radius: 0px">
      <q-toolbar class="shadow-2 q-py-sm q-px-md">
        <q-toolbar-title class="text-weight-bold">
          <strong>Confirm abandon workflow</strong>
        </q-toolbar-title>
        <q-btn icon="close" round flat class="btn--no-hover" @click="showConfirm = false"></q-btn>
      </q-toolbar>
      <q-card-actions class="q-pb-md">
        <q-btn
          color="primary"
          :loading="abandonLoading"
          @click="abandon"
          size="md"
          class="q-ml-sm"
          no-caps
          label="Yes"
        ></q-btn>
        <q-btn flat size="md" class="btn--no-hover" style="height: 40px" no-caps @click="showConfirm = false">No</q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import dayjs from 'dayjs';
import { copyToClipboard } from 'quasar';
import { abandonWorkflow, getWorkFlows, refreshStatus } from './api';
import Status from './components/Status.vue';
import { supportCntrlClick } from '@/shared/helpers';
import StatusSelect from '../allItems/components/StatusSelect.vue';
import DatePicker from '../allItems/components/DatePicker.vue';
import WorkflowsBtn from '@/shared/components/WorkflowsBtn.vue';
import WorkflowNameSelect from './components/WorkflowNameSelect.vue';

export default {
  name: 'WorkFlows',
  components: {
    Status,
    StatusSelect,
    DatePicker,
    WorkflowsBtn,
    WorkflowNameSelect,
  },
  data() {
    return {
      showAdvancedFilter: false,
      statusLoadingWorkflowId: undefined,
      abandonLoading: false,
      showConfirm: false,
      currentWorkfowUuid: undefined,
      loading: false,
      pagination: {
        sortBy: 'createdAt',
        descending: true,
        page: 1,
        rowsPerPage: 20,
      },
      filters: {
        itemId: undefined,
        uuid: undefined,
        cancelling: undefined,
        workflowIds: [],
        status: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        inputVariables: undefined,
      },
      rows: [],
      timeout: undefined,
      filteredNames: [],
      cols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Workflow name',
          name: 'workflowId',
          field: 'workflowId',
          align: 'left',
        },
        {
          label: 'Actor',
          name: 'actor',
          field: 'actor',
          align: 'left',
        },
        {
          label: 'Created at',
          name: 'createdAt',
          field: 'createdAt',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updatedAt',
          field: 'updatedAt',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Process Instance Key',
          name: 'processInstanceKey',
          field: 'processInstanceKey',
          align: 'left',
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
    };
  },
  methods: {
    showRelatedWorkflows(workflow) {
      this.resetFilters();
      this.filters.itemId = workflow.uuid;
      this.filterTable();
    },
    validateUuid(value) {
      const uuidRegex =
        // eslint-disable-next-line
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return value?.length ? uuidRegex.test(value) || 'Must be a valid UUID' : true;
    },
    resetFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = key === 'workflowIds' ? [] : undefined;
      });
      this.filters.cancelling = false;
      this.filterTable();
    },
    openDatePopup(type) {
      if (type === 'from') {
        this.$refs.popupFrom?.show();
      } else if (type === 'to') {
        this.$refs.popupTo?.show();
      }
    },
    copyUuid(uuid) {
      copyToClipboard(uuid).then(() => {
        this.$notify('info', 'Info', 'Workflow UUID was copied');
      });
    },
    async abandon() {
      this.abandonLoading = true;
      try {
        const { uuid, status } = await abandonWorkflow(this.currentWorkfowUuid);
        const updWorkfow = this.rows.find((workflow) => workflow.uuid === uuid);
        updWorkfow.status = status;
        this.$notify('positive', 'Success', 'Workflow was abandon successfull.');
        this.showConfirm = false;
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.abandonLoading = false;
      }
    },
    confirmAbandon(uuid) {
      this.currentWorkfowUuid = uuid;
      this.showConfirm = true;
    },
    async reloadStatus(id) {
      this.statusLoadingWorkflowId = id;
      try {
        const { uuid, status } = await refreshStatus(id);
        const updWorkfow = this.rows.find((workflow) => workflow.uuid === uuid);
        updWorkfow.status = status;
        this.$notify('positive', 'Success', 'Workflow status refreshed.');
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.statusLoadingWorkflowId = undefined;
      }
    },
    goToWorkflow(event, uuid) {
      supportCntrlClick(event, `/workflows/${uuid}`);
    },
    goToSco(id) {
      window.open(`${process.env.VUE_APP_OASIS_URL}/${id}`, '_blank');
    },
    onWorkflowIdsUpdate(value) {
      this.filters.workflowIds = Array.isArray(value) ? value : [];
      this.filterTable();
    },
    filterTable() {
      this.$refs.form.validate().then((valid) => {
        if (valid) {
          this.loading = true;
          if (this.timeout) clearTimeout(this.timeout);
          this.timeout = setTimeout(async () => {
            this.$refs.workflowTable.requestServerInteraction();
          }, 1500);
        }
      });
    },
    reloadWorkflows() {
      this.$refs.workflowTable.requestServerInteraction();
    },
    async loadWorkflows(props) {
      this.loading = true;
      try {
        const limit = props.pagination.rowsPerPage;
        const { sortBy, descending } = props.pagination;
        const skip = props.pagination.page === 1 ? 0 : (props.pagination.page - 1) * props.pagination.rowsPerPage;
        const { itemId, uuid, cancelling, workflowIds, status, dateFrom, dateTo, inputVariables } = this.filters;
        const { count, data } = await getWorkFlows(
          limit,
          skip,
          sortBy,
          descending,
          itemId,
          uuid,
          workflowIds,
          status,
          cancelling,
          dateFrom,
          dateTo,
          inputVariables,
        );
        this.pagination.rowsNumber = count;
        this.rows = data;
        this.pagination.page = props.pagination.page;
        this.pagination.rowsPerPage = props.pagination.rowsPerPage;
        this.pagination.sortBy = sortBy;
        this.pagination.descending = descending;
        this.$router.replace({ query: this.buildQueryFromFilters() });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (key === 'cancelling') {
          if (this.filters.cancelling === null) {
            query[key] = false;
          } else if (this.filters.cancelling) {
            query[key] = true;
          } else {
            query[key] = 'showAll';
          }
        } else if (key === 'workflowIds') {
          const workflowIds = Array.isArray(this.filters.workflowIds) ? this.filters.workflowIds : [];
          if (workflowIds.length) {
            query[key] = workflowIds.join(',');
          }
        } else if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
  },
  async mounted() {
    const query = this.$route.query;
    if (query.dateFrom || query.dateTo) {
      this.showAdvancedFilter = true;
    }
    const sortFields = ['createdAt', 'updatedAt'];

    for (const field of sortFields) {
      if (query?.[field]) {
        this.pagination.sortBy = field;
        this.pagination.descending = query[field] !== 'ASC';
      }
    }
    this.filters.itemId = query.itemId || undefined;
    const queryWorkflowIds = query.workflowIds || query.workflowId;
    this.filters.workflowIds = queryWorkflowIds
      ? Array.isArray(queryWorkflowIds)
        ? queryWorkflowIds.flatMap((value) => value.split(',').filter(Boolean))
        : queryWorkflowIds.split(',').filter(Boolean)
      : [];
    this.filters.status = query.status || undefined;
    this.filters.cancelling = query.cancelling === 'false' ? null : query.cancelling === 'true' ? true : false;
    this.filters.dateFrom = query.dateFrom || undefined;
    this.filters.dateTo = query.dateTo || undefined;
    this.filters.inputVariables = query.inputVariables || undefined;
    if (this.$route.query.itemId) {
      this.filters.itemId = this.$route.query.itemId;
    }

    try {
      this.reloadWorkflows();
    } catch (error) {
      this.$notify('negative', 'Error', error.message);
    }
  },
};
</script>

<style lang="scss" scoped>
.instance-key {
  color: $blue;
}
.showBanner {
  .fixed-table {
    height: calc(100vh - 360px);
  }
}
.fixed-table {
  height: calc(100vh - 300px);
}
</style>

<style lang="scss">
.filters {
  .select-option__selected {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .q-select {
    .q-field__native {
      max-height: 24px;
      span {
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
